import { motion, useReducedMotion } from 'motion/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipImage } from '@/components/ClipImage';
import { Lightbox } from '@/components/Lightbox';
import { Meta } from '@/components/Meta';
import { Reveal } from '@/components/Reveal';
import { products } from '@/data/products';
import { studio } from '@/data/studio';
import { useLang } from '@/hooks/useLang';
import { cn } from '@/lib/cn';

function FloatingProduct({ src, alt }: { src: string; alt: string }) {
  const reduce = useReducedMotion();
  return (
    <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden bg-paper-deep">
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        animate={reduce ? undefined : { y: [0, -18, 0] }}
        transition={{ duration: 6, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
        className="max-h-full max-w-full object-contain p-10 md:p-16"
      />
    </div>
  );
}

export function DesignPage() {
  const { t } = useTranslation();
  const lang = useLang();
  const [lightbox, setLightbox] = useState<number | null>(null);

  return (
    <>
      <Meta title={t('meta.designTitle')} description={t('meta.designDesc')} />

      <header className="u-container pt-[22vh] pb-12">
        <p className="eyebrow text-brass">{t('design.eyebrow')}</p>
        <h1 className="mt-5 font-display text-6xl italic md:text-8xl">{t('design.title')}</h1>
        <p className="mt-8 max-w-xl text-lg leading-relaxed text-ink-soft">{t('design.intro')}</p>
      </header>

      {products.map((p, i) => {
        const flip = i % 2 === 1;
        return (
          <section
            key={p.slug}
            className="u-container grid items-center gap-10 py-[7vh] md:grid-cols-2 md:gap-16"
          >
            <div className={flip ? 'md:order-2' : ''}>
              <FloatingProduct src={p.image} alt={p.name} />
            </div>
            <div className={flip ? 'md:order-1' : ''}>
              <Reveal>
                <p className="eyebrow text-brass">{p.tagline[lang]}</p>
                <h2 className="mt-4 font-display text-6xl italic md:text-7xl">{p.name}</h2>
                <p className="mt-6 max-w-md text-lg leading-relaxed text-ink-soft">
                  {p.description[lang]}
                </p>
                <ul className="mt-8 space-y-2.5 border-t border-line pt-6 text-sm">
                  {p.specs.map((s) => (
                    <li key={s.en} className="flex items-baseline gap-3 text-ink-soft">
                      <span className="text-brass">—</span> {s[lang]}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-sm text-stone">{p.meta[lang]}</p>
                {p.links && (
                  <div className="mt-7 flex flex-wrap gap-3">
                    {p.links.map((l) => (
                      <a
                        key={l.label}
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-full border border-ink px-6 py-2.5 text-xs uppercase tracking-[0.18em] transition-colors hover:bg-ink hover:text-paper"
                      >
                        {l.label}
                      </a>
                    ))}
                  </div>
                )}
              </Reveal>
            </div>
          </section>
        );
      })}

      <section className="u-container pt-[8vh] pb-[16vh]">
        <p className="eyebrow mb-10 text-brass">{t('design.galleryEyebrow')}</p>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {studio.productImages.map((src, i) => (
            <Reveal key={src} delay={(i % 3) * 0.06}>
              <button
                type="button"
                onClick={() => setLightbox(i)}
                data-cursor="hover"
                aria-label={`${t('design.open')} ${i + 1}`}
                className={cn('group block w-full cursor-pointer')}
              >
                <ClipImage
                  src={src}
                  alt={`Product ${i + 1}`}
                  className="aspect-square w-full"
                  imgClassName="transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105"
                />
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <Lightbox images={studio.productImages} index={lightbox} onChange={setLightbox} />
    </>
  );
}
